import { Link } from 'react-router-dom';
import { Puzzle } from 'lucide-react';
import { problemGrid as defaultProblemGrid } from '@/config-defaults';
import { useConfig } from '@/lib/siteData';
import { useLiveTarget } from '@/lib/liveTarget';
import { getIcon } from '@/lib/icons';

/**
 * Homepage "What I help solve" — spec `homepage.section_order` item 3.
 *
 * Sits between the audience pathways ("who are you") and the offers ("what can
 * you buy"), and answers the third question a visitor actually arrives with:
 * "is my problem one you work on?"
 *
 * Every tile resolves. Each carries a preferred `href` — its specialist page —
 * and a `fallbackHref` that is always live. Four of the six specialist pages
 * seed unpublished, so without the pairing this section would render two tiles
 * at launch and look broken. Tiles upgrade to the page link the moment the
 * owner publishes it, with no redeploy.
 *
 * Rendered as text-forward tiles rather than another card grid: this sits
 * between two card grids already, and three identical slabs in a row is the
 * homepage's real risk.
 */
export function ProblemGrid({ index }: { index?: number } = {}) {
  const config = useConfig('problem_grid', defaultProblemGrid);
  const items = config.items?.length ? config.items : defaultProblemGrid.items;
  const { isLive } = useLiveTarget();

  if (config.visible === false || items.length === 0) return null;

  return (
    <section className="w-full py-16 md:py-20 bg-surface-elevated border-t border-surface-border-gray">
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-10">
          <span className="font-mono-label text-xs uppercase tracking-[0.2em] text-text-light">
            {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
            {config.label}
          </span>
          <h2 className="font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-3 leading-[1.1] tracking-tight">
            {config.heading}
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-x-10 gap-y-7 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => {
            const Icon = getIcon(item.iconName, Puzzle);
            // Prefer the specialist page; fall back while it is unpublished.
            const href = item.href && isLive(item.href) ? item.href : item.fallbackHref;
            return (
              <Link
                key={item.title}
                to={href}
                className="group block border-t border-surface-border-gray pt-4 transition-colors duration-150 hover:border-accent-blue"
              >
                <Icon className="w-5 h-5 text-accent-blue mb-3" />
                <h3 className="font-heading text-base font-semibold text-text-primary mb-1.5 group-hover:text-accent-blue transition-colors duration-150">
                  {item.title}
                </h3>
                <p className="font-body text-sm text-text-gray leading-relaxed">
                  {item.description}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
